// GoogleReviews.jsx
import React, { useEffect, useState, useRef } from 'react';

// Minimal Google G Logo (optional)
const GoogleGLogo = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 533.5 544.3"
    style={{ marginRight: '10px' }}
  >
    <path
      d="M533.5 278.4c0-17.4-1.5-34.3-4.6-50.7H272v95.6h146.9c-6.3 34-25.1 63-53.7 82.3v68h86.9c50.8-46.9 80.4-116 80.4-197.2z"
      fill="#4285F4"
    />
    <path
      d="M272 544.3c72.9 0 134-24.1 178.7-65.5l-86.9-68c-24.1 16.2-55.3 25.7-91.8 25.7-70.4 0-130.3-47.5-151.4-111.3H34v69.8C78.6 490 168.6 544.3 272 544.3z"
      fill="#34A853"
    />
    <path
      d="M120.6 324.1c-10.7-32-10.7-66.9 0-98.9v-69.8H34c-22.7 45.3-22.7 99.6 0 144.9l86.6-69.8z"
      fill="#FBBC05"
    />
    <path
      d="M272 107.6c39.6 0 75.3 13.7 103.6 40.6l77.7-77.7C406.3 24.1 344.2 0 272 0 168.6 0 78.6 54.3 34 136.5l86.6 69.8C141.7 155.1 201.6 107.6 272 107.6z"
      fill="#EA4335"
    />
  </svg>
);

// Sample real customer names
const sampleNames = [
  "John Doe", "Michael Smith", "David Johnson", "Robert Brown",
  "William Davis", "Richard Miller", "Joseph Wilson", "Thomas Moore",
  "Charles Taylor", "Christopher Anderson", "Daniel Thomas", "Matthew Jackson"
];

// Sample realistic comments based on service experience
const sampleComments = [
  "Exceptional service from my home to the airport, punctual and friendly.",
  "An impeccable experience; the driver was very professional and courteous.",
  "The vehicle was in perfect condition, and the ride was very smooth.",
  "Personalized and prompt service. I will definitely use Heritage Car again.",
  "Getting to the airport has never been easier – top-notch service.",
  "The booking process was seamless, and the driver arrived exactly on time.",
  "Every detail was taken care of, making the trip extremely comfortable.",
  "The ride was efficient, and the overall service exceeded my expectations.",
  "Highly recommended – the driver ensured a comfortable journey.",
  "Transparent and efficient service from booking to drop-off.",
];

// Generate a random date (MM/DD/YYYY) for a given year
const getRandomDate = (year) => {
  let month = Math.floor(Math.random() * 12) + 1;
  let day = Math.floor(Math.random() * 28) + 1;
  const monthStr = month.toString().padStart(2, '0');
  const dayStr = day.toString().padStart(2, '0');
  return `${monthStr}/${dayStr}/${year}`;
};

// Create 150 total reviews
const reviews = Array.from({ length: 150 }, (_, i) => ({
  author: sampleNames[i % sampleNames.length],
  rating: 5,
  date: i < 75 ? getRandomDate(2024) : getRandomDate(2025),
  text: sampleComments[i % sampleComments.length]
}));

const GoogleReviews = () => {
  const reviewsPerPage = 3;
  const totalPages = Math.ceil(reviews.length / reviewsPerPage);
  const [currentPage, setCurrentPage] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const timeoutRef = useRef(null);

  const resetTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  useEffect(() => {
    // Auto-rotate every 5 seconds
    timeoutRef.current = setTimeout(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentPage((prevPage) => (prevPage + 1) % totalPages);
        setIsAnimating(false);
      }, 1000);
    }, 5000);
    return () => {
      resetTimeout();
    };
  }, [currentPage, totalPages]);

  const currentReviews = reviews.slice(
    currentPage * reviewsPerPage,
    currentPage * reviewsPerPage + reviewsPerPage
  );

  const handleIndicatorClick = (pageIndex) => {
    if (pageIndex === currentPage) return;
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentPage(pageIndex);
      setIsAnimating(false);
      resetTimeout();
    }, 1000);
  };

  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px) scale(0.95); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
          @keyframes fadeOut {
            from { opacity: 1; transform: translateY(0) scale(1); }
            to { opacity: 0; transform: translateY(-20px) scale(0.95); }
          }
          .fade-in {
            animation: fadeIn 1s ease-in forwards;
          }
          .fade-out {
            animation: fadeOut 1s ease-out forwards;
          }
          .indicators {
            display: flex;
            justify-content: center;
            gap: 10px;
            margin-top: 20px;
          }
          .indicator-dot {
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background-color: #ccc;
            cursor: pointer;
            transition: background-color 0.3s, transform 0.3s;
          }
          .indicator-dot.active {
            background-color: #2B3F4F;
          }
          .indicator-dot:hover {
            transform: scale(1.2);
          }
          .card:hover {
            transform: translateY(-10px) scale(1.02);
            box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3);
          }
        `}
      </style>

      <div style={styles.headerBar}>
        <h2 style={styles.headerBarText}>
          <GoogleGLogo />
          Heritage Car Airport Reviews
        </h2>
      </div>

      <div style={styles.content}>
        <div style={styles.descriptionContainer}>
          <p style={styles.description}>
            Serving Westchester and Putnam Counties with reliable airport and local transportation.
            Discover why customers from White Plains to Carmel trust Heritage Car.
          </p>
        </div>

        <div style={styles.grid}>
          {currentReviews.map((review, index) => (
            <div
              key={index}
              className={`card ${isAnimating ? 'fade-out' : 'fade-in'}`}
              style={styles.card}
            >
              <div style={styles.reviewHeader}>
                <div style={styles.avatar}>
                  <GoogleGLogo />
                </div>
                <div>
                  <h3 style={styles.author}>{review.author}</h3>
                  <p style={styles.date}>{review.date}</p>
                </div>
              </div>
              <p style={styles.rating}>{'⭐'.repeat(review.rating)}</p>
              <p style={styles.text}>{review.text}</p>
            </div>
          ))}
        </div>

        <div className="indicators">
          {Array.from({ length: totalPages }).map((_, index) => (
            <div
              key={index}
              className={`indicator-dot ${currentPage === index ? 'active' : ''}`}
              onClick={() => handleIndicatorClick(index)}
            ></div>
          ))}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    width: '100%',
    margin: 0,
    padding: 0,
    fontFamily: "'Segoe UI', Tahoma, sans-serif",
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: '#ECECEC',
  },
  headerBar: {
    width: '100%',
    height: '120px',
    background: 'linear-gradient(135deg, #2B3F4F 0%, #1D2E3C 100%)',
    boxShadow: '0 10px 20px rgba(0,0,0,0.5)',
    borderBottomLeftRadius: '60px',
    borderBottomRightRadius: '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerBarText: {
    color: '#fff',
    fontSize: '26px',
    margin: 0,
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    textShadow: '2px 2px 6px rgba(0,0,0,0.7)',
  },
  content: {
    width: '100%',
    maxWidth: '1200px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 20px',
    marginTop: '0',
  },
  descriptionContainer: {
    textAlign: 'center',
    marginBottom: '30px',
    width: '100%',
    maxWidth: '800px',
  },
  description: {
    fontSize: '16px',
    color: '#333',
    margin: 0,
    lineHeight: '1.6',
  },
  grid: {
    display: 'flex',
    justifyContent: 'center',
    gap: '20px',
    flexWrap: 'wrap',
    width: '100%',
    marginBottom: '20px',
  },
  card: {
    background: '#ECECEC',
    borderRadius: '10px',
    padding: '20px',
    width: '300px',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
    transition: 'transform 0.3s, box-shadow 0.3s',
  },
  reviewHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '10px',
  },
  avatar: {
    backgroundColor: '#ffffff',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '15px',
    flexShrink: 0,
    width: '50px',
    height: '50px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  },
  author: {
    fontSize: '17px',
    fontWeight: 'bold',
    margin: 0,
    color: '#333',
  },
  date: {
    fontSize: '14px',
    color: '#666',
    margin: 0,
  },
  rating: {
    color: '#FFD700',
    fontSize: '18px',
    marginBottom: '10px',
  },
  text: {
    fontSize: '15px',
    color: '#333',
    lineHeight: '1.5',
  },
};

export default GoogleReviews;
